import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import { appendCapKeysToFormData } from "../../../../../core/service/utils/fileUpload";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { EmvKernelProfileEntity } from "../../types/EmvKernelProfileEntity";
import { EmvKernelProfileDtoToEntity } from "../mappers/EmvKernelProfileDtoToEntity";
import { EmvKernelProfileEntityToDto } from "../mappers/EmvKernelProfileEntityToDto";

const EmvKernelProfileApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        EmvKernelProfileLoad: builder.query<EmvKernelProfileEntity, FormQuery>({
            providesTags: [ResourceName.EMV_KERNEL_PROFILES],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return EmvKernelProfileDtoToEntity(response);
            },
        }),
        EmvKernelProfileSave: builder.mutation<EmvKernelProfileEntity, FormMutation<EmvKernelProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}`;
                const method = "POST";

                const body = EmvKernelProfileEntityToDto(mutation.data);

                const formData = new FormData();
                appendCapKeysToFormData(formData, mutation.data);

                formData.append("dto", new Blob([JSON.stringify(body)], { type: "application/json" }));

                return {
                    url,
                    method,
                    body: formData,
                };
            },
            invalidatesTags: [ResourceName.EMV_KERNEL_PROFILES],
        }),
        EmvKernelProfileUpdate: builder.mutation<EmvKernelProfileEntity, FormMutation<EmvKernelProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${mutation.data.id}`;
                const method = "PUT";

                const body = EmvKernelProfileEntityToDto(mutation.data);

                const formData = new FormData();
                appendCapKeysToFormData(formData, mutation.data);

                formData.append("dto", new Blob([JSON.stringify(body)], { type: "application/json" }));

                return {
                    url,
                    method,
                    body: formData,
                };
            },
            invalidatesTags: [ResourceName.EMV_KERNEL_PROFILES],
        }),
        EmvKernelProfileApacLoad: builder.query<EmvKernelProfileEntity, FormQuery>({
            providesTags: [ResourceName.EMV_KERNEL_PROFILES],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return EmvKernelProfileDtoToEntity(response);
            },
        }),
        EmvKernelProfileApacSave: builder.mutation<EmvKernelProfileEntity, FormMutation<EmvKernelProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}`;
                const method = "POST";

                const body = EmvKernelProfileEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.EMV_KERNEL_PROFILES],
        }),
        EmvKernelProfileApacUpdate: builder.mutation<EmvKernelProfileEntity, FormMutation<EmvKernelProfileEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${mutation.data.id}`;
                const method = "PUT";

                const body = EmvKernelProfileEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.EMV_KERNEL_PROFILES],
        }),
    }),
});

export const {
    useEmvKernelProfileLoadQuery,
    useEmvKernelProfileSaveMutation,
    useEmvKernelProfileUpdateMutation,
    useEmvKernelProfileApacLoadQuery,
    useEmvKernelProfileApacSaveMutation,
    useEmvKernelProfileApacUpdateMutation,
} = EmvKernelProfileApi;
